import Aura from '@openng/optimus-ui-themes/aura';
import { definePreset } from '@openng/optimus-ui-themes';
import type { Preset } from '@openng/optimus-ui-themes/types';

export const AppThemePreset: Preset = definePreset(Aura, {
  primitive: {
    blue: {
      50: '#EFF6FF',
      100: '#DBEAFE',
      200: '#BFDBFE',
      300: '#93C5FD',
      400: '#60A5FA',
      500: '#3B82F8',
      600: '#2563EB',
      700: '#1D4ED8',
      800: '#1E40AF',
      900: '#1E3A8A',
      950: '#172554',
    },

    blueGray: {
      50: '#F8FAFC',
      100: '#F1F5F9',
      200: '#E2E8F0',
      300: '#CBD5E1',
      400: '#94A3B8',
      500: '#64748B',
      600: '#475569',
      700: '#334155',
      800: '#1E293B',
      900: '#0F172A',
      950: '#020617',
    },

    green: {
      50: '#F0FDF4',
      100: '#DCFCE7',
      200: '#BBF7D0',
      300: '#86EFAC',
      400: '#4ADE80',
      500: '#22C55E',
      600: '#16A34A',
      700: '#15803D',
      800: '#166534',
      900: '#14532D',
      950: '#052E16',
    },

    yellow: {
      50: '#FEFCE8',
      100: '#FEF9C3',
      200: '#FEF08A',
      300: '#FDE047',
      400: '#FACC15',
      500: '#EAB308',
      600: '#CA8A04',
      700: '#A16207',
      800: '#854D0E',
      900: '#713F12',
      950: '#422006',
    },

    cyan: {
      50: '#ECFEFF',
      100: '#CFFAFE',
      200: '#A5F3FC',
      300: '#67E8F9',
      400: '#22D3EE',
      500: '#06B6D4',
      600: '#0891B2',
      700: '#0E7490',
      800: '#155E75',
      900: '#164E63',
      950: '#083344',
    },

    pink: {
      50: '#FDF2F8',
      100: '#FCE7F3',
      200: '#FBCFE8',
      300: '#F9A8D4',
      400: '#F472B6',
      500: '#EC4899',
      600: '#DB2777',
      700: '#BE185D',
      800: '#9D174D',
      900: '#831843',
      950: '#500724',
    },

    indigo: {
      50: '#EEF2FF',
      100: '#E0E7FF',
      200: '#C7D2FE',
      300: '#A5B4FC',
      400: '#818CF8',
      500: '#6366F1',
      600: '#4F46E5',
      700: '#4338CA',
      800: '#3730A3',
      900: '#312E81',
      950: '#1E1B4B',
    },

    red: {
      50: '#FEF2F2',
      100: '#FEE2E2',
      200: '#FECACA',
      300: '#FCA5A5',
      400: '#F87171',
      500: '#EF4444',
      600: '#DC2626',
      700: '#B91C1C',
      800: '#991B1B',
      900: '#7F1D1D',
      950: '#450A0A',
    },

    teal: {
      50: '#F0FDFA',
      100: '#CCFBF1',
      200: '#99F6E4',
      300: '#5EEAD4',
      400: '#2DD4BF',
      500: '#14B8A6',
      600: '#0D9488',
      700: '#0F766E',
      800: '#115E59',
      900: '#134E4A',
      950: '#042F2E',
    },

    orange: {
      50: '#FFF7ED',
      100: '#FFEDD5',
      200: '#FED7AA',
      300: '#FDBA74',
      400: '#FB923C',
      500: '#F97316',
      600: '#EA580C',
      700: '#C2410C',
      800: '#9A3412',
      900: '#7C2D12',
      950: '#431407',
    },

    purple: {
      50: '#FAF5FF',
      100: '#F3E8FF',
      200: '#E9D5FF',
      300: '#D8B4FE',
      400: '#C084FC',
      500: '#A855F7',
      600: '#9333EA',
      700: '#7E22CE',
      800: '#6B21A8',
      900: '#581C87',
      950: '#3B0764',
    },

    gray: {
      50: '#F9FAFB',
      100: '#F3F4F6',
      200: '#E5E7EB',
      300: '#D1D5DB',
      400: '#9CA3AF',
      500: '#6B7280',
      600: '#4B5563',
      700: '#374151',
      800: '#1F2937',
      900: '#111827',
      950: '#030712',
    },

    sky: {
      50: '#F0F9FF',
      100: '#E0F2FE',
      200: '#BAE6FD',
      300: '#7DD3FC',
      400: '#38BDF8',
      500: '#0EA5E9',
      600: '#0284C7',
      700: '#0369A1',
      800: '#075985',
      900: '#0C4A6E',
      950: '#082F49',
    },
  },

  semantic: {
    primary: {
      50: '{blue.50}',
      100: '{blue.100}',
      200: '{blue.200}',
      300: '{blue.300}',
      400: '{blue.400}',
      500: '{blue.500}',
      600: '{blue.600}',
      700: '{blue.700}',
      800: '{blue.800}',
      900: '{blue.900}',
      950: '{blue.950}',
    },

    secondary: {
      50: '{blueGray.50}',
      100: '{blueGray.100}',
      200: '{blueGray.200}',
      300: '{blueGray.300}',
      400: '{blueGray.400}',
      500: '{blueGray.500}',
      600: '{blueGray.600}',
      700: '{blueGray.700}',
      800: '{blueGray.800}',
      900: '{blueGray.900}',
      950: '{blueGray.950}',
    },

    success: {
      50: '{green.50}',
      100: '{green.100}',
      200: '{green.200}',
      300: '{green.300}',
      400: '{green.400}',
      500: '{green.500}',
      600: '{green.600}',
      700: '{green.700}',
      800: '{green.800}',
      900: '{green.900}',
      950: '{green.950}',
    },

    info: {
      50: '{sky.50}',
      100: '{sky.100}',
      200: '{sky.200}',
      300: '{sky.300}',
      400: '{sky.400}',
      500: '{sky.500}',
      600: '{sky.600}',
      700: '{sky.700}',
      800: '{sky.800}',
      900: '{sky.900}',
      950: '{sky.950}',
    },

    warning: {
      50: '{orange.50}',
      100: '{orange.100}',
      200: '{orange.200}',
      300: '{orange.300}',
      400: '{orange.400}',
      500: '{orange.500}',
      600: '{orange.600}',
      700: '{orange.700}',
      800: '{orange.800}',
      900: '{orange.900}',
      950: '{orange.950}',
    },

    danger: {
      50: '{red.50}',
      100: '{red.100}',
      200: '{red.200}',
      300: '{red.300}',
      400: '{red.400}',
      500: '{red.500}',
      600: '{red.600}',
      700: '{red.700}',
      800: '{red.800}',
      900: '{red.900}',
      950: '{red.950}',
    },

    help: {
      50: '{purple.50}',
      100: '{purple.100}',
      200: '{purple.200}',
      300: '{purple.300}',
      400: '{purple.400}',
      500: '{purple.500}',
      600: '{purple.600}',
      700: '{purple.700}',
      800: '{purple.800}',
      900: '{purple.900}',
      950: '{purple.950}',
    },

    colorScheme: {
      light: {
        primary: {
          color: '{blue.500}',
          contrastColor: '#ffffff',
          hoverColor: '{blue.600}',
          activeColor: '{blue.700}',
        },
        primaryText: {
          color: '{blue.500}',
        },
        secondary: {
          color: '{blueGray.500}',
          contrastColor: '#ffffff',
          hoverColor: '{blueGray.600}',
          activeColor: '{blueGray.700}',
        },
        secondaryText: {
          color: '{blueGray.500}',
        },
        success: {
          color: '{green.500}',
          contrastColor: '#ffffff',
          hoverColor: '{green.600}',
          activeColor: '{green.700}',
        },
        successText: {
          color: '{green.500}',
        },
        info: {
          color: '{sky.500}',
          contrastColor: '#ffffff',
          hoverColor: '{sky.600}',
          activeColor: '{sky.700}',
        },
        infoText: {
          color: '{sky.500}',
        },
        warning: {
          color: '{orange.500}',
          contrastColor: '#ffffff',
          hoverColor: '{orange.600}',
          activeColor: '{orange.700}',
        },
        warningText: {
          color: '{orange.500}',
        },
        danger: {
          color: '{red.500}',
          contrastColor: '#ffffff',
          hoverColor: '{red.600}',
          activeColor: '{red.700}',
        },
        dangerText: {
          color: '{red.500}',
        },
        help: {
          color: '{purple.500}',
          contrastColor: '#ffffff',
          hoverColor: '{purple.600}',
          activeColor: '{purple.700}',
        },
        helpText: {
          color: '{purple.500}',
        },
        surface: {
          border: '{blueGray.300}',
          defaultText: '{blueGray.800}',
          secondaryText: '{blueGray.500}',
          inverseText: '{blueGray.50}',
        },
        highlight: {
          background: '{blue.50}',
          focusBackground: '{blue.100}',
          color: '{blue.700}',
          focusColor: '{blue.800}',
        },
      },
      dark: {
        primary: {
          color: '{blue.400}',
          contrastColor: '{surface.900}',
          hoverColor: '{blue.300}',
          activeColor: '{blue.200}',
        },
        primaryText: {
          color: '{blue.400}',
        },
        secondary: {
          color: '{blueGray.400}',
          contrastColor: '{surface.900}',
          hoverColor: '{blueGray.300}',
          activeColor: '{blueGray.200}',
        },
        secondaryText: {
          color: '{blueGray.400}',
        },
        success: {
          color: '{green.400}',
          contrastColor: '{surface.900}',
          hoverColor: '{green.300}',
          activeColor: '{green.200}',
        },
        successText: {
          color: '{green.400}',
        },
        info: {
          color: '{sky.400}',
          contrastColor: '{surface.900}',
          hoverColor: '{sky.300}',
          activeColor: '{sky.200}',
        },
        infoText: {
          color: '{sky.400}',
        },
        warning: {
          color: '{orange.400}',
          contrastColor: '{surface.900}',
          hoverColor: '{orange.300}',
          activeColor: '{orange.200}',
        },
        warningText: {
          color: '{orange.400}',
        },
        danger: {
          color: '{red.400}',
          contrastColor: '{surface.900}',
          hoverColor: '{red.300}',
          activeColor: '{red.200}',
        },
        dangerText: {
          color: '{red.400}',
        },
        help: {
          color: '{purple.400}',
          contrastColor: '{surface.900}',
          hoverColor: '{purple.300}',
          activeColor: '{purple.200}',
        },
        helpText: {
          color: '{purple.400}',
        },
        surface: {
          border: '{blueGray.300}',
          defaultText: '{blueGray.800}',
          secondaryText: '{blueGray.500}',
          inverseText: '{blueGray.50}',
        },
        highlight: {
          background: 'color-mix(in srgb, {blue.400}, transparent 84%)',
          focusBackground: 'color-mix(in srgb, {blue.400}, transparent 76%)',
          color: 'rgba(255,255,255,.87)',
          focusColor: 'rgba(255,255,255,.87)',
        },
      },
    },
  },
});
